<script>
export default {
  name: 'OrderSearch'
}
</script>

<script setup>
import { ref, reactive, onMounted, onUnmounted, nextTick, watch } from 'vue';

import Pagination from '@parts/Pagination.vue';
import OrderNavigation from '@parts/OrderNavigation.vue';
import GoodsList from '@parts/GoodsList.vue';
import axios from 'axios';
import { apiError } from '@functions/apiError.js';
import ApiErrorModal from '@parts/ApiErrorModal.vue';
import { modalUtil } from '@functions/modalUtil.js';
import SearchNumOverModal from '@parts/modal/SearchNumOverModal.vue'
import { url } from '@/config/urls';
import { message } from '@functions/message.js';
import {
  createHardScannerKeywordWatcher,
  resolveBarcodeParams,
  toHalfWidthDigits,
} from '@functions/barcodeScan.js';
import { Capacitor } from '@capacitor/core';

const { showModal } = modalUtil();

// 画面ID
const screenId = ref("OrderSearch");

// エラーメッセージの状態管理
const errorMessages = ref([]);

// 定数定義
const pageSizeArray = [50, 100, 200];

// Prop定義
const props = defineProps({
  userDataUrl: {
    type: String,
    default: url.api.userData
  },
  orderSearchApiUrl: {
    type: String,
    default: url.api.orderSearch
  },
  orderFavoriteListApiUrl: {
    type: String,
    default: url.api.orderFavorite.list
  },
  goodsTypesApiUrl: {
    type: String,
    default: url.api.goodsTypes
  },
  orderCartCountApiUrl: {
    type: String,
    default: url.api.orderCart.count
  },
  errorUrl: {
    type: String,
    default: url.error
  },
  loadingIcon: {
    type: String,
    default: url.static.loadingIcon
  }
});

// ユーザー情報
const user = reactive({
  isPermittedOrder: null,  // null: 未取得, true: 発注, false: 在庫問合せ
  isPermittedStockInquiry: null,  // null: 未取得, true: 在庫問合せ可, false: 不可
  deliveryDateAllowedFlg: null  // null: 未取得, 1: 納品日指定可能, 0: 納品日指定不可
});

// ユーザー情報取得APIの呼び出し
const getUserInfo = async () => {
  try {
    const response = await axios.get(props.userDataUrl);
    user.isPermittedOrder = response.data.response.isPermittedOrder;
    user.isPermittedStockInquiry = response.data.response.isPermittedStockInquiry;
    user.deliveryDateAllowedFlg = response.data.response.deliveryDateAllowedFlg;
  } catch (error) {
    apiError(error, errorMessages);
  }
}

// 商品一覧取得APIリクエストパラメータ
const condition = ref({
  words: null,
  onlyOrderedFlg: false,
  includeDiscontinuedGoods: false,
  goodsFavId: null,
  goodsTypeCode: null,
  dispensingPackagingUnitCode: null,
  janCode: null,
  size: 50,
  page: 1
});

// フォーム入力値
const formInputs = ref({
  words: '',
  onlyOrderedFlg: false,
  includeDiscontinuedGoods: false,
  goodsFavId: '',
  goodsTypeCode: ''
});

// 検索キーワード入力欄の要素参照
const wordsInputRef = ref(null);

// エラー
const errors = ref({
  words: '',
  goodsFavId: '',
  goodsTypeCode: ''
});

// ページネーション
const pagingParameter = ref({
  pageSize: 50,
  pageNum: 1
});
const totalPage = ref(1);

// 商品分類マスタデータ
const goodsTypes = ref([]);

// お気に入りグループ
const goodsFavorites = ref([]);

// 検索結果
const goodsList = ref([]);
const totalNum = ref(0);

// ローディングフラグ
const isLoading = ref(false);

// ローディングフラグ更新
const handleUpdateLoading = (loading) => {
  isLoading.value = loading;
}

// エラー処理
const handleApiError = (error) => {
	apiError(error, errorMessages);
};

// カート商品 品目数
const cartGoodsTotalRecord = ref(0);
// カート商品集計
const countCartGoods = () => {
  axios.get(props.orderCartCountApiUrl)
    .then((response) => {
      cartGoodsTotalRecord.value = parseInt(response.data.response.totalNum);
    })
    .catch((error) => {
      apiError(error, errorMessages);
    });
}

// お気に入りマスタデータ取得処理
const getGoodsFavorites = () => {
  axios.get(props.orderFavoriteListApiUrl)
    .then((response) => {
      goodsFavorites.value = response.data.response.goodsFavorites;
    })
    .catch((error) => {
      apiError(error, errorMessages);
    });
}

// 商品分類マスタデータ取得処理
const getGoodsTypes = () => {
  axios.get(props.goodsTypesApiUrl)
    .then((response) => {
      goodsTypes.value = response.data.response.goodsTypes;
    })
    .catch((error) => {
      apiError(error, errorMessages);
    });
}

// 商品一覧取得処理
const getGoodsList = async () => {
  handleUpdateLoading(true);
  try {
    const response = await axios.get(props.orderSearchApiUrl, { params: condition.value });
    if (response.data.response.goodsList) goodsList.value = response.data.response.goodsList;
    totalNum.value = parseInt(response.data.response.totalNum);
    pagingParameter.value.pageNum = parseInt(response.data.response.currentPage);
    totalPage.value = Math.ceil(totalNum.value / pagingParameter.value.pageSize);
    totalPage.value = totalPage.value ? totalPage.value : 1;
    if (totalNum.value > 40000) {
      goodsList.value = [];
      nextTick(() => {
        showModal('searchNumOverModal');
      });
      return;
    }
  } catch (error) {
    apiError(error, errorMessages);
  } finally {
    handleUpdateLoading(false);
  }
};

// 検索キーワード入力欄Enterキー押下時の処理
const onKeyDownEnterWords = (event) => {
  if (isLoading.value) {
    event.preventDefault();
    return;
  }
  if (hardScannerKeywordWatcher.isScanDetectionPending()) {
    event.preventDefault();
    return;
  }
  // 検索時フォーカスアウト(仮想キーボードの非表示)
  event.target.blur();

  clickSearchBtn();
}

// ボタンクリック
const clickSearchBtn = () => {
  wordsValidation();
  goodsFavoritesValidation();
  goodsTypesValidation();
  if (Object.values(errors.value).some(value => value !== '')) {
    return;
  }
  setCondition();
  getGoodsList();
}

const clickClearBtn = () => {
  formInputs.value.words = '';
  formInputs.value.onlyOrderedFlg = false;
  formInputs.value.includeDiscontinuedGoods = false;
  formInputs.value.goodsFavId = '';
  formInputs.value.goodsTypeCode = '';
  condition.value.dispensingPackagingUnitCode = null;
  condition.value.janCode = null;
}

// ページネーション処理
const onChangePageNum = (page) => {
  if (!Number.isFinite(page)) {
    return;
  }
  condition.value.page = page;
  getGoodsList();

  pagingParameter.value.pageNum = page;

  window.scroll({
      top: 0,
      behavior: 'smooth'
  });
}

const onChangePageSize = (selected) => {
  condition.value.page = 1;
  condition.value.size = selected;
  getGoodsList();

  pagingParameter.value.pageNum = 1;
  pagingParameter.value.pageSize = selected;

}

// 検索条件のセット
const setCondition = () => {
  condition.value.words = formInputs.value.words || null;
  condition.value.onlyOrderedFlg = formInputs.value.onlyOrderedFlg;
  condition.value.includeDiscontinuedGoods = formInputs.value.includeDiscontinuedGoods;
  condition.value.goodsFavId = formInputs.value.goodsFavId || null;
  condition.value.goodsTypeCode = formInputs.value.goodsTypeCode || null;
  condition.value.dispensingPackagingUnitCode = null;
  condition.value.janCode = null;
  condition.value.size = pagingParameter.value.pageSize || 50;
  condition.value.page = 1;
  pagingParameter.value.pageNum = 1;
}

// 検索キーワードバリデーション
const wordsValidation = () => {
  const maxLength = 255;
  errors.value.words = '';
  if (formInputs.value.words.length > maxLength) {
    let errorMessage = message.error.validation.string.length;
    errorMessage = errorMessage.replace('{max}', maxLength);
    errors.value.words = errorMessage;
  }
}

// お気に入りバリデーション
const goodsFavoritesValidation = () => {
  errors.value.goodsFavId = '';
  if (!formInputs.value.goodsFavId) { return; }
  if (!goodsFavorites.value.some(goodsFavorite => goodsFavorite.id === formInputs.value.goodsFavId)) {
    errors.value.goodsFavId = message.error.not_found;
  }
}

// 商品分類バリデーション
const goodsTypesValidation = () => {
  errors.value.goodsTypeCode = '';
  if (!formInputs.value.goodsTypeCode) { return; }
  if (!goodsTypes.value.some(goodsType => goodsType.code === formInputs.value.goodsTypeCode)) {
    errors.value.goodsTypeCode = message.error.not_found;
  }
}

// 初期処理
onMounted(async () => {
  await getUserInfo();
  if (!user.isPermittedStockInquiry) {
    return;
  }

  getGoodsTypes();
  if (user.isPermittedOrder) {
    countCartGoods();
    getGoodsFavorites();
  }
});

const focusWordsInput = () => {
  const isNativeApp = Capacitor.isNativePlatform();
  if (isNativeApp) {
    return;
  }
  nextTick(() => {
    if (wordsInputRef.value) {
      wordsInputRef.value.focus();
    }
  });
};

const onBarcodeScanned = async () => {
  formInputs.value.words = toHalfWidthDigits(formInputs.value.words || '');
  const raw = formInputs.value.words;
  const parsed = resolveBarcodeParams(raw);
  if (!parsed.ok) {
    errors.value.words = parsed.error;
    formInputs.value.words = '';
    focusWordsInput();
    return;
  }
  errors.value.words = '';
  formInputs.value.words = '';
  goodsFavoritesValidation();
  goodsTypesValidation();
  if (Object.values(errors.value).some((value) => value !== '')) {
    focusWordsInput();
    return;
  }
  setCondition();
  condition.value.words = null;
  condition.value.janCode = parsed.janCode;
  condition.value.dispensingPackagingUnitCode = parsed.dispensingPackagingUnitCode;

  await getGoodsList();
  focusWordsInput();
  condition.value.janCode = null;
  condition.value.dispensingPackagingUnitCode = null;
};

const hardScannerKeywordWatcher = createHardScannerKeywordWatcher({
  getKeyword: () => formInputs.value.words,
  onScanComplete: () => {
    void onBarcodeScanned();
  },
});

/** コピペではハードスキャナ自動検索を走らせない */
const skipHardScanAfterPaste = ref(false);

const markKeywordPasteForHardScan = () => {
  skipHardScanAfterPaste.value = true;
  hardScannerKeywordWatcher.cancelPendingScan();
};

const onKeywordFieldInput = (event) => {
  if (event.inputType === 'insertFromPaste') {
    markKeywordPasteForHardScan();
  }
};

watch(
  () => formInputs.value.words,
  (newVal) => {
    if (skipHardScanAfterPaste.value) {
      skipHardScanAfterPaste.value = false;
      hardScannerKeywordWatcher.cancelPendingScan();
      return;
    }
    hardScannerKeywordWatcher.onKeywordChange(newVal);
  }
);

onUnmounted(() => hardScannerKeywordWatcher.dispose());

watch(
  () => user.isPermittedOrder,
  (isPermitted) => {
    if (isPermitted === null) return;
    // ユーザー権限取得後に DOM が出揃ってからキーワード欄へフォーカス
    focusWordsInput();
  },
  { flush: 'post' }
);

</script>

<template>

  <div id="contents1" class="bg1" v-if="user.isPermittedStockInquiry">
    <OrderNavigation
      :screenId = "screenId"
      :isPermittedOrder = user.isPermittedOrder
      :cartGoodsTotalRecord = cartGoodsTotalRecord
    ></OrderNavigation>
      
    <h2 v-if="!user.isPermittedOrder" class="com_title1 com_mgb5">在庫問合せ</h2>
    <h2 v-else class="com_title1 com_mgb5">発　　注</h2>

    <div class="com_result1">
      <div class="com_inner2">
      <!-- searchForm -->
        <template v-if="user.isPermittedOrder">
          <div class="group3">
            <div>
              <input
                  ref="wordsInputRef"
                  type="text"
                  placeholder="メーカー名、商品名、商品コード、GTIN"
                  class="form-control com_form1"
                  v-model="formInputs.words"
                  :class="{ error: errors.words }"
                  @input="onKeywordFieldInput"
                  @paste="markKeywordPasteForHardScan"
                  @keydown.enter="onKeyDownEnterWords"
                  enterkeyhint="search">
              <div class="com_validate_msg1 error" v-if="errors.words">{{ errors.words }}</div>
            </div>
            <div>
              <div class="com_form_check1"><input type="checkbox" v-model="formInputs.onlyOrderedFlg"><label>発注履歴から検索</label></div>
              <div class="com_form_check1"><input type="checkbox" v-model="formInputs.includeDiscontinuedGoods"><label>中止品を含む</label></div>
            </div>
          </div>
          <div class="group4">
            <select class="form-select com_form1" v-model="formInputs.goodsFavId" :class="{ error: errors.goodsFavId }">
              <option value="">お気に入りグループ</option>
              <option v-for="fav in goodsFavorites" :key="fav.id" :value="fav.id">
                {{ fav.name }}
              </option>
            </select>
            <select class="form-select com_form1" v-model="formInputs.goodsTypeCode" :class="{ error: errors.goodsTypeCode }">
              <option value="" selected>商品分類:すべて</option>
              <option v-for="goodsType in goodsTypes" :key="goodsType.code" :value="goodsType.code">
                {{ goodsType.name }}
              </option>
            </select>
            <button type="button" class="btn com_btn1" @click.prevent="clickSearchBtn">検索</button>
            <button type="button" class="btn com_btn2" @click.prevent="clickClearBtn">クリア</button>
          </div>
          <div style="display: flex;">
            <div class="com_validate_msg1 error" v-if="errors.goodsFavId">{{ errors.goodsFavId }}</div>
            <div class="com_validate_msg1 error" v-if="errors.goodsTypeCode">{{ errors.goodsTypeCode }}</div>
          </div>
        </template>
        <template v-else>
          <div class="inquiry-keyword-line">
            <div>
              <input
                  ref="wordsInputRef"
                  type="text"
                  placeholder="メーカー名、商品名、商品コード、GTIN"
                  class="form-control com_form1"
                  v-model="formInputs.words"
                  :class="{ error: errors.words }"
                  @input="onKeywordFieldInput"
                  @paste="markKeywordPasteForHardScan"
                  @keydown.enter="onKeyDownEnterWords"
                  enterkeyhint="search">
            </div>
            <div>
              <div class="com_form_check1"><input type="checkbox" v-model="formInputs.includeDiscontinuedGoods"><label>中止品を含む</label></div>
            </div>
          </div>
          <div style="display: flex;">
            <div class="com_validate_msg1 error" v-if="errors.words">{{ errors.words }}</div>
            <div class="com_validate_msg1 error" v-if="errors.goodsTypeCode">{{ errors.goodsTypeCode }}</div>
          </div>
          <div class="inquiry-action-line">
            <select class="form-select com_form1" v-model="formInputs.goodsTypeCode" :class="{ error: errors.goodsTypeCode }">
              <option value="" selected>商品分類:すべて</option>
              <option v-for="goodsType in goodsTypes" :key="goodsType.code" :value="goodsType.code">
                {{ goodsType.name }}
              </option>
            </select>
            <button type="button" class="btn com_btn1" @click.prevent="clickSearchBtn">検索</button>
            <button type="button" class="btn com_btn2" @click.prevent="clickClearBtn">クリア</button>
          </div>
        </template>

      </div>
    </div>
    <div class="com_inner4 clearfix">

      <template v-if="goodsList.length === 0">
        <p class="com_fs16 text-center">検索項目を入力してください。</p>
      </template>
      <template v-else>
        <p class="com_fs16 text-center order-results-text">検索結果は以下の通りです</p>

        <Pagination
          :isShowFirstAndLastBtn="true"
          :isShowPaginationInfo="true"
          :totalNum="totalNum"
          :currentPage="pagingParameter.pageNum"
          :totalPage="totalPage"
          :pageSize="pagingParameter.pageSize"
          :pageSizeSelectList="pageSizeArray"
          @changePageNum="onChangePageNum"
          @changePageSize="onChangePageSize"
        ></Pagination>

        <GoodsList
          :screenId = "screenId"
          :isPermittedOrder = user.isPermittedOrder
          :deliveryDateAllowedFlg = user.deliveryDateAllowedFlg
          :goodsList = goodsList
          :goodsTypes = goodsTypes
          :goodsFavorites = goodsFavorites
          @updateLoading="handleUpdateLoading"
          @countCartGoods="countCartGoods"
          @apiError="handleApiError"
        ></GoodsList>

        <Pagination
          :isShowFirstAndLastBtn="true"
          :isShowPaginationInfo="true"
          :totalNum="totalNum"
          :currentPage="pagingParameter.pageNum"
          :totalPage="totalPage"
          :pageSize="pagingParameter.pageSize"
          :pageSizeSelectList="pageSizeArray"
          @changePageNum="onChangePageNum"
          @changePageSize="onChangePageSize"
        ></Pagination>

      </template>

    </div>
  </div>

  <SearchNumOverModal
    :total-num="totalNum"
  ></SearchNumOverModal>

  <ApiErrorModal
    :messages="errorMessages"
  ></ApiErrorModal>

  <Transition>
    <div class="loading-animation" v-show="isLoading">
      <div class="show-icon-box">
        <img :src="props.loadingIcon" />
      </div>
    </div>
  </Transition>

</template>
